import { useCallback, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import useAppStore from "./microsite-store";

/**
 * Hook to associate the current microsite user with 3rd-party/external identifiers
 * (e.g. `gigya_id`) via `/microsite/associate-external-user-data/`.
 *
 * Reads external IDs from searchParamsOnInit stored in the microsite store
 * (which captures initial URL search params before they're removed).
 */
export function useAssociateExternalUserData() {
  const [associating, setAssociating] = useState(false);
  const [error, setError] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    user,
    entityInfo,
    setAssociateExternalUserDataAttempts,
    associateExternalUserDataAttempts,
    searchParamsOnInit,
  } = useAppStore((state) => ({
    user: state.user,
    entityInfo: state.entityInfo,
    associateExternalUserDataAttempts: state.associateExternalUserDataAttempts,
    setAssociateExternalUserDataAttempts:
      state.setAssociateExternalUserDataAttempts,
    searchParamsOnInit: state.searchParamsOnInit,
  }));

  // Prevent duplicate calls for the same payload within a single session.
  const lastPayloadKeyRef = useRef(null);

  const idsFromSearchParams = useMemo(() => {
    // Only source gigya_id from searchParamsOnInit
    const gigyaId = searchParamsOnInit?.gigya_id || null;
    return {
      gigya_id: gigyaId,
    };
  }, [searchParamsOnInit]);

  const hasAnyExternalIdInSearchParams = useMemo(() => {
    return Boolean(idsFromSearchParams.gigya_id);
  }, [idsFromSearchParams]);

  const associateExternalUserData = useCallback(
    async (externalIds, { additionalPayload = {} } = {}) => {
      setError(null);

      // Get latest values from store to ensure we have the most up-to-date user
      const currentState = useAppStore.getState();
      const currentUser = currentState.user;
      const currentEntityInfo = currentState.entityInfo;

      const payload = {
        ...(externalIds || {}),
        ...additionalPayload,
        user_id: currentUser?.id,
        organization_id: currentEntityInfo?.organization_id,
        campaign_effort_id: currentEntityInfo?.campaign_effort_id,
      };

      // Remove empty ids so the server only sees meaningful keys.
      Object.keys(payload).forEach((k) => {
        if (
          payload[k] === null ||
          payload[k] === undefined ||
          payload[k] === ""
        ) {
          delete payload[k];
        }
      });

      // Hard requirements to associate
      if (!payload.organization_id || !payload.campaign_effort_id) {
        return false;
      }

      // Must have at least one external id key
      if (!payload.gigya_id) {
        return false;
      }

      const payloadKey = JSON.stringify(payload);
      if (lastPayloadKeyRef.current === payloadKey) {
        return true;
      }

      setAssociating(true);
      try {
        const { data } = await axios.post(
          `/microsite/associate-external-user-data/`,
          payload
        );
        const res = data?.response?.[0] || data?.response || data;

        if (res?.user === false) {
          setAssociateExternalUserDataAttempts(
            associateExternalUserDataAttempts + 1
          );
        }
        lastPayloadKeyRef.current = payloadKey;
        return true;
      } catch (err) {
        setError(err);
        return false;
      } finally {
        setAssociating(false);
      }
    },
    [user?.id, entityInfo?.organization_id, entityInfo?.campaign_effort_id]
  );

  const associateFromSearchParams = useCallback(
    async ({ additionalPayload = {} } = {}) => {
      return associateExternalUserData(idsFromSearchParams, {
        additionalPayload,
      });
    },
    [associateExternalUserData, idsFromSearchParams]
  );

  return {
    associating,
    error,
    idsFromSearchParams,
    hasAnyExternalIdInSearchParams,
    associateExternalUserData,
    associateFromSearchParams,
    setError,
  };
}
